import { compact, filter, isEmpty, map, uniq, values } from "lodash";
import React, { useCallback, useMemo } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import ContactComponent from "../../../components/ContactComponent";
import { getLabelColorStyle } from "../../../components/labels/utils";
import { useAppearance } from "../../../context/appearance";
import { useCommissionTemplates } from "../../../context/commissionTemplates";
import { useContacts } from "../../../context/contacts";
import { useInstalls } from "../../../context/installs";
import { Contact } from "../../../utils/types";

const InstallContactsTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { contacts } = useContacts();
  const { installs } = useInstalls();
  const { isDark } = useAppearance();
  const { getCommissionValueLabel } = useCommissionTemplates();

  const currentInstall = useMemo(
    () => (props.match.params.id ? installs[props.match.params.id] : undefined),
    [installs, props.match.params.id]
  );

  const linkedContacts = useMemo(() => {
    if (!currentInstall) return [];

    const commissionContactIds = map(
      currentInstall.commissions,
      "contact_uuid"
    );

    const linkedLocationContactIds = map(
      values(contacts).filter(({ linked_location_ids }) =>
        linked_location_ids.includes(currentInstall.location_uuid)
      ),
      "uuid"
    );

    return compact(
      uniq(commissionContactIds.concat(linkedLocationContactIds)).map(
        (uuid) => contacts[uuid]
      )
    );
  }, [contacts, currentInstall]);

  const renderContactRow = useCallback(
    (contact: Contact) => {
      const contactCommissions = filter(currentInstall?.commissions || [], {
        contact_uuid: contact.uuid
      });

      return (
        <tr key={contact.uuid}>
          <td className="px-6 py-4 whitespace-nowrap">
            <Link
              className="relative flex items-center space-x-3 group"
              to={`/contacts/${contact.uuid}`}
            >
              <ContactComponent contactId={contact.uuid} />
            </Link>
          </td>

          <td className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center flex-wrap">
              {contactCommissions.map((commission) => (
                <div
                  key={commission.uuid}
                  className="inline-flex items-center rounded-full font-medium px-3 text-sm mr-2"
                  style={getLabelColorStyle("#d73aaa", isDark)}
                >
                  {getCommissionValueLabel(commission)}
                </div>
              ))}
            </div>
          </td>

          <td className="px-6 py-4 whitespace-nowrap">
            <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
              {contact.email ?? "-"}
            </p>
          </td>

          <td className="px-6 py-4 whitespace-nowrap">
            <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
              {contact.phone ?? "-"}
            </p>
          </td>
        </tr>
      );
    },
    [currentInstall?.commissions, getCommissionValueLabel, isDark]
  );

  if (!currentInstall) return null;

  return (
    <>
      <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="w-full py-2 align-middle inline-block min-w-full">
          {!isEmpty(linkedContacts) ? (
            <div className="shadow w-full overflow-x-auto sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
                <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      <div className="flex items-center">
                        <span className="mr-1">
                          {linkedContacts.length} linked contact
                          {linkedContacts.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Commission
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Email
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Phone
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                  {linkedContacts.map(renderContactRow)}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex">
              <p className="text-sm text-gray-500 dark:text-gray-300">
                There are no contacts linked to the location of this install.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default InstallContactsTab;
